import styled, { css } from "styled-components";

/**
 * Estilos da pagina publica da peca (destino do QR da etiqueta).
 *
 * Cores em HEX cru, sem token do tema, de proposito: esta pagina e SEMPRE
 * escura, independente do que o visitante tenha configurado — ele nao tem
 * conta no ERP nem preferencia salva, e a tela vai ser lida em pe, no
 * galpao, muitas vezes contra a luz. O laranja e o mesmo `sunset` do design
 * system no modo escuro (#FB923C), para a pagina publica nao parecer de
 * outro produto.
 *
 * As cores da PECA (face e miolo do MDF) tambem sao fixas, e por um motivo
 * mais forte: elas representam um objeto fisico. Um MDF cru e bege
 * amarronzado no mundo real, em qualquer tema.
 */

const LARANJA = "#FB923C";
const FUNDO = "#0B0C0F";
const FUNDO_CARTAO = "#15171C";
const BORDA = "rgba(255, 255, 255, 0.10)";
const TEXTO = "#F4F4F5";
const TEXTO_FRACO = "rgba(244, 244, 245, 0.62)";

// As cores da PEÇA (laminado, miolo cru, arestas) vivem em `Peca3D.jsx` e
// `texturas.js` — o desenho agora é WebGL, não CSS.

export const Pagina = styled.div`
  min-height: 100vh;
  background: ${FUNDO};
  color: ${TEXTO};
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
`;

export const Topo = styled.header`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border-bottom: 1px solid ${BORDA};

  .num { font-size: 15px; font-weight: 800; letter-spacing: 0.02em; }
  .desc { font-size: 13px; color: ${TEXTO_FRACO}; flex: 1; min-width: 0; }
`;

export const Selo = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid ${LARANJA};
  color: ${LARANJA};
  white-space: nowrap;
`;

// ---------- metade de cima: o 3D ----------

export const Palco3D = styled.div`
  position: relative;
  /* Metade da tela, como pedido — com piso para nao sumir em tela baixa. */
  height: max(48vh, 300px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  /* O giro e o zoom sao nossos: sem isto o navegador rouba o gesto e a
     pagina rola em vez de a peca girar. */
  touch-action: none;
  cursor: grab;
  user-select: none;
  background:
    radial-gradient(ellipse at 50% 40%, #1C1F26 0%, ${FUNDO} 70%);

  &:active { cursor: grabbing; }
`;

export const Vazio = styled.div`
  color: ${TEXTO_FRACO};
  font-size: 13px;
  text-align: center;
  padding: 0 20px;

  ${({ $rodape }) => $rodape && css`
    position: absolute;
    bottom: 8px;
    left: 0;
    right: 0;
    font-size: 11px;
    line-height: 1.35;
  `}
`;

/**
 * Ocupa o lugar do 3D enquanto o chunk do three.js baixa (React.lazy).
 * Mesma altura do Palco3D para a página não pular quando ele chegar.
 */
export const PalcoEsqueleto = styled.div`
  height: max(48vh, 300px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: ${TEXTO_FRACO};
  background:
    radial-gradient(ellipse at 50% 40%, #1C1F26 0%, ${FUNDO} 70%);
`;

/**
 * Moldura do fallback 2D (aparelho sem WebGL). Fundo claro porque o
 * VisualPecaFita usa as variáveis do tema do ERP, desenhadas para fundo
 * claro — jogá-lo direto no escuro apagaria as cotas.
 */
export const CartaoFallback = styled.div`
  min-height: max(48vh, 300px);
  margin: 12px 16px;
  padding: 18px;
  border-radius: 12px;
  background: #F4F4F5;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;

  p {
    margin: 0;
    text-align: center;
    font-size: 12px;
    color: #52525B;
  }
`;

export const Controles = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 6px;
`;

export const BotaoCena = styled.button`
  min-width: 40px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${BORDA};
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.45);
  color: ${TEXTO};
  font: inherit;
  font-size: 13px;
  cursor: pointer;

  &:hover { border-color: ${LARANJA}; }
`;

export const Dica = styled.p`
  margin: 0;
  padding: 8px 16px;
  text-align: center;
  font-size: 12px;
  color: ${TEXTO_FRACO};
  border-bottom: 1px solid ${BORDA};
`;

// ---------- metade de baixo: os dados ----------

export const Dados = styled.main`
  padding: 14px 16px 32px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const Bloco = styled.section`
  background: ${FUNDO_CARTAO};
  border: 1px solid ${BORDA};
  border-radius: 12px;
  padding: 12px 14px;
`;

export const TituloBloco = styled.h2`
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${LARANJA};
`;

export const Grade = styled.dl`
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px 14px;

  dt {
    font-size: 11px;
    color: ${TEXTO_FRACO};
    margin-bottom: 2px;
  }
  dd {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }
`;

export const ListaFita = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const ChipFita = styled.li`
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${({ $tem }) => ($tem ? LARANJA : BORDA)};
  color: ${({ $tem }) => ($tem ? LARANJA : TEXTO_FRACO)};

  /* Sem fita nao e so "apagado": e informacao de producao. O tracejado
     repete o codigo visual do desenho 2D da Gestao. */
  border-style: ${({ $tem }) => ($tem ? "solid" : "dashed")};
`;

export const Fotos = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
`;

export const FotoBotao = styled.button`
  position: relative;
  aspect-ratio: 1;
  padding: 0;
  border: 1px solid ${BORDA};
  border-radius: 10px;
  overflow: hidden;
  background: #000;
  cursor: pointer;

  img { width: 100%; height: 100%; object-fit: cover; display: block; }

  .ph {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 11px;
    color: ${TEXTO_FRACO};
  }

  &:hover { border-color: ${LARANJA}; }
`;

export const FotoAberta = styled.div`
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  img { max-width: 100%; max-height: 100%; object-fit: contain; }
`;

export const Fechar = styled.button`
  position: absolute;
  top: calc(10px + env(safe-area-inset-top, 0px));
  right: 10px;
  min-width: 44px;
  min-height: 44px;
  border: none;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
`;

export const Estado = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px;
  text-align: center;
  background: ${FUNDO};
  color: ${TEXTO};
  font-family: system-ui, -apple-system, sans-serif;

  strong { font-size: 17px; }
  span { font-size: 14px; color: ${TEXTO_FRACO}; max-width: 34ch; }
`;
